import { useLingui } from '@lingui/react/macro';
import * as React from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import Icon from 'components/Icon';
import { classNames } from 'utils/ui/css';
import { myCameraStream } from 'state/video';
import { formatDeviceLabel, type StreamState } from 'utils/ui/media';

export interface DeviceSelectorProps {
  kind: 'video' | 'audio';
  className?: string;
}

/** AVS-4513: chevron next to the waiting-room mic/camera toggle that opens the list of input devices. */
const DeviceSelector: React.FC<DeviceSelectorProps> = ({ kind, className }) => {
  const { t } = useLingui();
  const media = myCameraStream();
  const { videoDevices = [], audioDevices = [] } = media.useState() as StreamState;
  const devices = kind === 'video' ? videoDevices : audioDevices;
  const selectedId = media.getSelectedDeviceId(kind);

  const handleChange = async (deviceId: string) => {
    try {
      await myCameraStream().switchDevice(kind, deviceId);
    } catch (error) {
      console.error(`Failed to switch ${kind} device:`, error);
    }
  };

  const buttonLabel = kind === 'video' ? t`Choose camera` : t`Choose microphone`;
  const fallbackLabel = (index: number) => (kind === 'video' ? t`Camera ${index + 1}` : t`Microphone ${index + 1}`);

  return (
    <Listbox value={selectedId ?? undefined} onChange={handleChange}>
      <div className={classNames('relative ml-2 size-11 shrink-0', className)}>
        <ListboxButton
          data-testid={kind === 'video' ? 'device-selector-camera' : 'device-selector-microphone'}
          aria-label={buttonLabel}
          title={buttonLabel}
          className="group flex size-11 shrink-0 items-center justify-start rounded focus:outline-none data-[focus]:ring-2 data-[focus]:ring-gray-900"
        >
          <Icon className="ex3d-icon--chevron size-4 rotate-90 bg-gray-900 group-data-[open]:-rotate-90" />
        </ListboxButton>

        {/* Inline, not anchored: an anchor portals to document.body, outside the app's shadow root and its styles. */}
        <ListboxOptions
          modal={false}
          className="absolute bottom-full left-0 z-10 mb-2 max-h-60 min-w-56 overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none"
        >
          {devices.length === 0 ? (
            <ListboxOption value={null} disabled className="px-3 py-2 text-gray-500">
              {kind === 'video' ? t`No camera available` : t`No microphone available`}
            </ListboxOption>
          ) : (
            devices.map((device, index) => (
              <ListboxOption
                key={device.id}
                value={device.id}
                data-testid={`device-selector-${kind}-option`}
                data-device-id={device.id}
                className="group flex cursor-pointer select-none items-center gap-2 px-3 py-2 text-gray-900 data-[focus]:bg-gray-100"
              >
                <span className="flex-1 truncate group-data-[selected]:font-semibold">
                  {formatDeviceLabel(device.label, fallbackLabel(index))}
                </span>
                <span aria-hidden="true">
                  <Icon className="ex3d-icon--check invisible size-4 bg-gray-900 group-data-[selected]:visible" />
                </span>
              </ListboxOption>
            ))
          )}
        </ListboxOptions>
      </div>
    </Listbox>
  );
};

export default DeviceSelector;
